import { ComponentType } from 'react';
import {
  getRoundedPrecision,
  intlAssetValueFormatting,
  PrecisionFloat,
  timeScalarOrNull,
  type TimeScalar,
} from '@bts-web/utils-formatting';
import { TransactionDetailsFieldVariants } from '../../../transaction-details';
import { optionalTimeFields } from '../../../transaction-details/utils/detailFieldBuilders';
import { TFunction } from 'i18next';
import { Locale } from '@bts-web/utils-lokalise';
import { SavingsPlanDetailsTranslations } from './getSavingsPlanDetailsTranslations';
import { SavingsPlanInfo } from '../components/SavingsPlanInfo/SavingsPlanInfo';
import { SavingsPlanItemType } from '../../types';
import { getAppConfig } from '@bts-web/core-app-config';

type SavingsPlanInfoProps = {
  className?: string;
  label: string;
};

export type SavingsPlanDetailEntity =
  | {
      kind: 'component';
      name: string;
      label: string;
      component: ComponentType<SavingsPlanInfoProps>;
    }
  | {
      kind: 'date-only';
      name: string;
      label: string;
      value: TimeScalar;
      disabled?: boolean;
    }
  | {
      kind: 'text';
      name: string;
      label: string;
      value: string;
      disabled?: boolean;
    };

const toSavingsPlanDateOnly = (
  field: ReturnType<typeof optionalTimeFields>[number],
  disabled?: boolean,
): SavingsPlanDetailEntity | null => {
  if (field.kind !== 'date-only') {
    return null;
  }

  return {
    kind: 'date-only',
    label: field.label,
    name: field.name,
    value: field.value,
    ...(disabled ? { disabled } : {}),
  };
};

const savingsPlanDateOnlyFields = (
  disabled: boolean,
  ...props: Parameters<typeof optionalTimeFields>
): SavingsPlanDetailEntity[] =>
  optionalTimeFields(...props).flatMap((field) => {
    const entity = toSavingsPlanDateOnly(field, disabled);

    return entity ? [entity] : [];
  });

const optionalTextField = (
  label: string,
  name: string,
  value: string | null | undefined,
): SavingsPlanDetailEntity[] =>
  value == null ? [] : [{ kind: 'text', label, name, value }];

export const formatAssetPrice = (
  symbol: string,
  price: PrecisionFloat,
  locale: string,
  currency?: string,
): string =>
  `1 ${symbol} = ${intlAssetValueFormatting(getRoundedPrecision(price), {
    locale,
    currency,
    fractionDigits: price?.precision,
  })}`;

export const getSavingsPlanTransactionDetailsFieldsData = ({
  translations,
  savingsPlan,
  locale,
  currency,
  translationUtil,
}: {
  translations: SavingsPlanDetailsTranslations;
  savingsPlan: SavingsPlanItemType;
  locale: Locale;
  currency?: string;
  translationUtil: TFunction;
}): SavingsPlanDetailEntity[] => {
  const { paymentDaysBeforeExecution = 3 } = getAppConfig();

  const createdAt = timeScalarOrNull(savingsPlan?.createdAt);

  const nextRecurrence = timeScalarOrNull(savingsPlan?.nextRecurrence);

  const nextPayment = timeScalarOrNull(savingsPlan?.notificationDate);

  const transactionDetailsFields: Record<string, string | null> = {
    paymentMethod: savingsPlan?.fiat?.name
      ? translationUtil('savings_plan_balance', {
          name: savingsPlan.fiat.name,
        })
      : null,
    currentValue:
      savingsPlan?.asset?.symbol && savingsPlan?.asset?.price
        ? formatAssetPrice(
            savingsPlan.asset.symbol,
            savingsPlan.asset.price,
            locale,
            currency,
          )
        : null,
  };

  const frequencyLabelMap: Partial<
    Record<NonNullable<SavingsPlanItemType['frequency']>, string>
  > = {
    MONTHLY: translations.monthly,
    WEEKLY: translations.weekly,
    ...(translations.biweekly ? { BIWEEKLY: translations.biweekly } : {}),
    ...(translations.daily ? { DAILY: translations.daily } : {}),
  };

  const frequencyLabel = savingsPlan?.frequency
    ? (frequencyLabelMap[savingsPlan.frequency] ?? translations.monthly)
    : translations.monthly;

  const isCancelled = savingsPlan?.status === 'CANCELED';

  return [
    ...optionalTextField(
      translations.currentValue,
      'savings-plan-current-value',
      transactionDetailsFields.currentValue,
    ),
    ...savingsPlanDateOnlyFields(
      false,
      translations.createdAt,
      'savings-plan-start-date',
      createdAt,
      TransactionDetailsFieldVariants.TRANSACTION_DATE,
    ),
    ...savingsPlanDateOnlyFields(
      isCancelled,
      translations.nextRecurrence,
      'savings-plan-next-recurrence',
      nextRecurrence,
      TransactionDetailsFieldVariants.TRANSACTION_DATE,
    ),
    ...savingsPlanDateOnlyFields(
      isCancelled,
      translations.nextPayment,
      'savings-plan-nextPayment',
      nextPayment,
      TransactionDetailsFieldVariants.TRANSACTION_DATE,
    ),
    {
      kind: 'component',
      label: translationUtil('savings_plan_info_label_@tenant', {
        days: paymentDaysBeforeExecution,
      }),
      name: 'info-field',
      component: SavingsPlanInfo,
    },
    {
      kind: 'text',
      label: translations.frequency,
      name: 'savings-plan-frequency',
      value: frequencyLabel,
    },
    ...optionalTextField(
      translations.paymentMethod,
      'savingsPlan-spread',
      transactionDetailsFields.paymentMethod,
    ),
  ];
};
